import useSWR from 'swr';
import { useBrandId } from 'AppProvider';
import buildCalendarUrl from './buildCalendarUrl';
import fetchCalendar from './fetchCalendar';
import { CalendarGrouping } from './types';

const fetchCalendarSWR = ([url, { brandId }]: [string, { brandId: string }]) =>
  fetchCalendar(url, { brandId });

/**
 * Fetches the guest "Schedule View" agenda for a single property-local day from the v3 calendar
 * endpoint. Mirrors the useSessions SWR pattern (conditional key, brand headers, error retry).
 */
const useScheduleCalendar = ({
  date,
  grouping = 'timeOfDay',
  categoryIds,
  vibeIds,
  skip = false,
}: {
  date?: string | null;
  grouping?: CalendarGrouping;
  categoryIds?: ReadonlyArray<string>;
  vibeIds?: ReadonlyArray<string>;
  skip?: boolean;
}) => {
  const brandId = useBrandId();
  const url =
    brandId && date && !skip
      ? buildCalendarUrl(brandId, date, { grouping, categoryIds, vibeIds })
      : null;

  const swr = useSWR(url ? [url, { brandId }] : null, fetchCalendarSWR, {
    // No keepPreviousData: changing the date should show the skeleton agenda while the new day
    // loads (the skeleton fills the agenda area, so the page doesn't collapse like the old text).
    onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
      if (retryCount >= 3) return;
      if (error?.status && error.status < 500) return;
      setTimeout(() => revalidate({ retryCount }), 2 ** retryCount * 1000);
    },
  });

  return {
    ...swr,
    day: swr.data ?? null,
    isLoadingCalendar: swr.isLoading,
  };
};

export default useScheduleCalendar;
